import type { DocumentTitleConfig } from './document-title-config';
import type { SubpagesLinksListProps } from './subpages-links-list';

import { Box, Heading } from '@chakra-ui/react';
import { useMemo } from 'react';

import { formatDocumentName } from '#utils/format-document-name';

import { DocumentTitleSubpagesPortal } from './document-title-subpages-portal';

export interface DocumentTitleBlockProps {
  config: DocumentTitleConfig;
  parentPath: string;
  children: SubpagesLinksListProps['children'];
  editorPlaceholderMuted?: boolean;
  isNewDocument: boolean;
  onCreateSubpage?: () => void;
  onNavigateToDocument?: (path: string) => void;
  onRequestRename?: () => void;
}

export function DocumentTitleBlock({
  config,
  parentPath,
  children: childDocs,
  editorPlaceholderMuted,
  isNewDocument,
  onCreateSubpage,
  onNavigateToDocument,
  onRequestRename,
}: DocumentTitleBlockProps) {
  const title = useMemo(() => {
    const explicitTitle = config.explicitTitle?.trim() ?? '';
    if (explicitTitle) {
      return explicitTitle;
    }

    const path = parentPath.trim();
    const labels = formatDocumentName(path);
    const lastLabel = labels.length > 0 ? labels[labels.length - 1] : '';
    return lastLabel || path || 'Untitled';
  }, [config.explicitTitle, parentPath]);

  return (
    <>
      <Heading
        as="h1"
        size="2xl"
        className="markdown-document-title-node__title"
      >
        {title}
      </Heading>
      <Box mt={1}>
        <DocumentTitleSubpagesPortal
          parentPath={parentPath}
          children={childDocs}
          editorPlaceholderMuted={editorPlaceholderMuted}
          isNewDocument={isNewDocument}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
          onRequestRename={onRequestRename}
        />
      </Box>
    </>
  );
}
